"use client";
import React, { useEffect, useId, useRef } from "react";
import { useGSAP } from "@/components/providers/gsap-provider";

const ExpertiseHeaderAnimated = () => {
  const { gsap, registerAnimation } = useGSAP();
  const svgId = useId().replace(/:/g, "");
  const paint0Id = `${svgId}-paint0`;
  const paint1Id = `${svgId}-paint1`;
  const paint2Id = `${svgId}-paint2`;
  const paint3Id = `${svgId}-paint3`;
  const paint4Id = `${svgId}-paint4`;
  const paint5Id = `${svgId}-paint5`;
  const paint6Id = `${svgId}-paint6`;
  const paint7Id = `${svgId}-paint7`;
  const paint8Id = `${svgId}-paint8`;
  const paint9Id = `${svgId}-paint9`;
  const trainBlurId = `${svgId}-trainBlur`;
  const purpleGlowId = `${svgId}-purpleGlow`;
  const svgRef = useRef<SVGSVGElement>(null);

  // Individual refs for each purple element - these will be animated as trains
  const line1Ref = useRef<SVGPathElement>(null);
  const line2Ref = useRef<SVGPathElement>(null);
  const line3Ref = useRef<SVGPathElement>(null);
  const line4Ref = useRef<SVGPathElement>(null);
  const line5Ref = useRef<SVGPathElement>(null);
  const line6Ref = useRef<SVGPathElement>(null);
  const line7Ref = useRef<SVGPathElement>(null);
  const line8Ref = useRef<SVGPathElement>(null);

  // Path refs for the purple elements to travel along
  const path1Ref = useRef<SVGPathElement>(null);
  const path2Ref = useRef<SVGPathElement>(null);
  const path3Ref = useRef<SVGPathElement>(null);
  const path4Ref = useRef<SVGPathElement>(null);
  const path5Ref = useRef<SVGPathElement>(null);
  const path6Ref = useRef<SVGPathElement>(null);
  const path7Ref = useRef<SVGPathElement>(null);
  const path8Ref = useRef<SVGPathElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    // Collect all purple line elements
    const purpleElements = [
      line1Ref.current,
      line2Ref.current,
      line3Ref.current,
      line4Ref.current,
      line5Ref.current,
      line6Ref.current,
      line7Ref.current,
      line8Ref.current,
    ].filter(Boolean); // Filter out null refs

    // Collect all path elements
    const pathElements = [
      path1Ref.current,
      path2Ref.current,
      path3Ref.current,
      path4Ref.current,
      path5Ref.current,
      path6Ref.current,
      path7Ref.current,
      path8Ref.current,
    ].filter(Boolean);

    const tl = gsap.timeline({
      repeat: -1,
      repeatDelay: 1,
    });

    // Hide the static purple elements completely
    purpleElements.forEach((el) => {
      if (!el) return;
      gsap.set(el, { opacity: 0 });
    });

    // Create train elements to travel along the paths
    pathElements.forEach((path, index) => {
      if (!path) return;

      // Create multiple trains for each path
      const trainCount = 3; // 3 trains per path

      for (let trainIndex = 0; trainIndex < trainCount; trainIndex++) {
        // Create a new train element
        const trainElement = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "rect"
        );
        trainElement.setAttribute("width", "20");
        trainElement.setAttribute("height", "3");
        trainElement.setAttribute("fill", "#8752FA"); // Use the original purple color
        trainElement.setAttribute("rx", "1.5");
        trainElement.setAttribute("ry", "1.5");
        trainElement.setAttribute("filter", `url(#${purpleGlowId})`);
        trainElement.setAttribute(
          "class",
          `train-element-${index}-${trainIndex}`
        );
        trainElement.style.opacity = "0";

        // Add glow element
        const glowElement = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "rect"
        );
        glowElement.setAttribute("width", "6");
        glowElement.setAttribute("height", "3");
        glowElement.setAttribute("fill", "#ffffff");
        glowElement.setAttribute("rx", "1.5");
        glowElement.setAttribute("ry", "1.5");
        glowElement.setAttribute("class", `train-glow-${index}-${trainIndex}`);
        glowElement.style.opacity = "0";

        // Add to SVG
        if (svgRef.current) {
          svgRef.current.appendChild(trainElement);
          svgRef.current.appendChild(glowElement);
        }

        // For paths on the left side (indices 4-7) and right side (indices 0-3)
        // We want all trains to flow from right to left (position 1 to 0)

        // All trains will now start from position 1 (right) and go to position 0 (left)
        const startPosition = 1;
        const endPosition = 0; // Create a sub-timeline for this train animation
        const trainTl = gsap.timeline({
          repeat: -1,
          repeatDelay: Math.random() * 4 + 2, // Random delay between 2-6 seconds
        });

        // Animate the train along the path
        trainTl
          .set([trainElement, glowElement], {
            opacity: 0,
            autoAlpha: 0,
          })
          .to([trainElement, glowElement], {
            opacity: 1,
            autoAlpha: 1,
            duration: 0.5,
          })
          .fromTo(
            [trainElement, glowElement],
            {
              motionPath: {
                path: path,
                align: path,
                alignOrigin: [0.5, 0.5],
                autoRotate: true,
                start: startPosition,
                end: startPosition,
              },
            },
            {
              motionPath: {
                path: path,
                align: path,
                alignOrigin: [0.5, 0.5],
                autoRotate: true,
                start: startPosition,
                end: endPosition,
              },
              duration: 8 + (index % 4) * 2,
              ease: "power1.inOut",
            }
          )
          .to(
            [trainElement, glowElement],
            {
              opacity: 0,
              autoAlpha: 0,
              duration: 0.5,
            },
            "-=0.5"
          );

        // Add the train animation to the main timeline with staggered starts
        // Use different timing offsets for each train on the same path
        tl.add(trainTl, 1 + index * 1.2 + trainIndex * 3 + Math.random() * 2);
      }
    });

    registerAnimation("expertiseHeaderAnimation", tl);

    return () => {
      tl.kill();
    };
  }, [gsap, registerAnimation]);

  return (
    <div className="absolute inset-0 -z-[1] hidden md:block">
      <svg
        ref={svgRef}
        width="100%"
        height="442"
        viewBox="0 0 1440 442"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full min-w-[1440px]"
        style={{
          width: "100vw",
          height: "442px",
          maxWidth: "none",
        }}
      >
        <defs>
          <linearGradient
            id={paint0Id}
            x1="1205.5"
            y1="125"
            x2="1205.5"
            y2="91"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint1Id}
            x1="1220"
            y1="227.5"
            x2="1254"
            y2="227.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint2Id}
            x1="1133.5"
            y1="129"
            x2="1133.5"
            y2="95"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint3Id}
            x1="1352"
            y1="352.5"
            x2="1386"
            y2="352.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint4Id}
            x1="26.5"
            y1="0"
            x2="26.5"
            y2="40"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0A001F" />
            <stop offset="1" stopColor="#0A001F" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint5Id}
            x1="235.5"
            y1="125"
            x2="235.5"
            y2="91"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint6Id}
            x1="221"
            y1="227.5"
            x2="187"
            y2="227.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint7Id}
            x1="307.5"
            y1="129"
            x2="307.5"
            y2="95"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint8Id}
            x1="89"
            y1="352.5"
            x2="55"
            y2="352.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8752FA" />
            <stop offset="1" stopColor="#8751F9" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id={paint9Id}
            x1="400.5"
            y1="180"
            x2="400.5"
            y2="220"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0A001F" />
            <stop offset="1" stopColor="#0A001F" stopOpacity="0" />
          </linearGradient>
          <filter id={trainBlurId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.529 0 0 0 0 0.322 0 0 0 0 0.98 0 0 0 1 0"
            />
          </filter>
          <filter
            id={purpleGlowId}
            x="-100%"
            y="-100%"
            width="300%"
            height="300%"
          >
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#8752FA" floodOpacity="0.9" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Right side paths - Static gray lines */}
        <path
          ref={path1Ref}
          d="M1067 202H1196.26C1201.78 202 1206.26 197.523 1206.26 192V24C1206.26 18.4772 1210.74 14 1216.26 14H9999"
          stroke="#414141"
        />
        <path
          ref={path2Ref}
          d="M1067 228.5H1351.5C1357.02 228.5 1361.5 224.023 1361.5 218.5V102C1361.5 96.4772 1365.98 92 1371.5 92H9999"
          stroke="#414141"
        />
        <path
          ref={path3Ref}
          d="M1067 216H1144.27C1149.8 216 1154.27 220.477 1154.27 226V343C1154.27 348.523 1158.75 353 1164.27 353H1450.98C1456.5 353 1460.98 357.477 1460.98 363V432C1460.98 437.523 1465.45 442 1470.98 442H9999"
          stroke="#414141"
        />
        <path
          ref={path4Ref}
          d="M1067 190H1123.78C1129.3 190 1133.78 185.523 1133.78 180V75C1133.78 69.4772 1138.26 65 1143.78 65H9999"
          stroke="#414141"
        />

        {/* Right side - Animated purple elements */}
        <path
          ref={line1Ref}
          d="M1206 125L1206 91"
          stroke={`url(#${paint0Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={line2Ref}
          d="M1220 228L1254 228"
          stroke={`url(#${paint1Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={line3Ref}
          d="M1134 129L1134 95"
          stroke={`url(#${paint2Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={line4Ref}
          d="M1352 353L1386 353"
          stroke={`url(#${paint3Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />

        <rect
          width="53"
          height="40"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 1067 180)"
          fill={`url(#${paint4Id})`}
        />

        {/* Left side paths - Static gray lines */}
        <path
          ref={path5Ref}
          d="M374 202H244.739C239.216 202 234.739 197.523 234.739 192V24C234.739 18.4772 230.262 14 224.739 14H-9999"
          stroke="#414141"
        />
        <path
          ref={path6Ref}
          d="M374 228.5H89.5C83.9772 228.5 79.5 224.023 79.5 218.5V102C79.5 96.4772 75.0228 92 69.5 92H-9999"
          stroke="#414141"
        />
        <path
          ref={path7Ref}
          d="M374 216H296.727C291.204 216 286.727 220.477 286.727 226V343C286.727 348.523 282.25 353 276.727 353H-9.97582C-15.4987 353 -19.9758 357.477 -19.9758 363V432C-19.9758 437.523 -24.453 442 -29.9758 442H-9999"
          stroke="#414141"
        />
        <path
          ref={path8Ref}
          d="M374 190H317.22C311.697 190 307.22 185.523 307.22 180V75C307.22 69.4772 302.743 65 297.22 65H-9999"
          stroke="#414141"
        />

        {/* Left side - Animated purple elements */}
        <path
          ref={line5Ref}
          d="M235 125L235 91"
          stroke={`url(#${paint5Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={line6Ref}
          d="M221 228L187 228"
          stroke={`url(#${paint6Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={line7Ref}
          d="M307 129L307 95"
          stroke={`url(#${paint7Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={line8Ref}
          d="M89 353L55 353"
          stroke={`url(#${paint8Id})`}
          strokeWidth="2"
          strokeLinecap="round"
        />

        <rect
          x="374"
          y="180"
          width="53"
          height="40"
          transform="rotate(90 374 180)"
          fill={`url(#${paint9Id})`}
        />
      </svg>
    </div>
  );
};

export default ExpertiseHeaderAnimated;
